import React from 'react';

import { Download as DownloadIcon } from '@mui/icons-material';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { isEmpty } from 'es-toolkit/compat';

import { parseNullableValue } from '@hrlogics/helper-utils';
import { FullscreenLoader } from '@hrlogics/react-components';

import { DateFormatPatterns } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';
import { formatDate } from '@clearverify/utils';

import {
  GeneratedReportMetaInfoFragment,
  PiiDataAccessActionType,
  TestVerificationRequestInfoFragment,
  VerificationRequestType,
} from 'src/api';
import { GeneratedReportPreviewCard } from 'src/components/generatedReport/GeneratedReportPreviewCard';
import { InboxItemEmptyContent } from 'src/components/inboxItem';
import { InfoBlock } from 'src/components/shared';
import { ModalId } from 'src/constants/modals';
import { useNotification } from 'src/hooks/app';
import { useEmployeeInfoForVerificationRequest } from 'src/hooks/employee';
import { useGeneratedReports } from 'src/hooks/generatedReport';
import { useModal } from 'src/hooks/shared';
import { TestVerificationRequestPdfPreviewModalArgs } from 'src/modals/testVerificationRequest';

interface TestVerificationRequestContentProps {
  inboxItemId: string;
  testVerificationRequest: TestVerificationRequestInfoFragment;
  loading: boolean;
}

export const TestVerificationRequestContent: React.FC<TestVerificationRequestContentProps> = ({
  inboxItemId,
  testVerificationRequest,
  loading,
}) => {
  const { spawnNotification } = useNotification();

  const { openModal } = useModal();

  const {
    generatedReportsMeta,
    downloadGeneratedReportFiles,
    downloadGeneratedReportFilesArchive,
  } = useGeneratedReports({
    inboxItemId,
  });
  const { employeeInfoForVerificationRequest, loading: employeeInfoForVerificationRequestLoading } =
    useEmployeeInfoForVerificationRequest(testVerificationRequest.employeeId);

  const handlePreview = React.useCallback(
    async (reportFile: GeneratedReportMetaInfoFragment) => {
      const [previewFile] = await downloadGeneratedReportFiles(
        reportFile.file.id,
        PiiDataAccessActionType.Viewed,
      );
      if (previewFile) {
        openModal(ModalId.TestVerificationRequestPdfPreviewModal, {
          generatedReportFile: previewFile,
        } satisfies TestVerificationRequestPdfPreviewModalArgs);
      }
    },
    [downloadGeneratedReportFiles, openModal],
  );

  const handleDownloadReport = React.useCallback(async () => {
    if (!generatedReportsMeta.length) {
      spawnNotification("There's no reports yet", 'error');
      return;
    }

    await downloadGeneratedReportFilesArchive(
      generatedReportsMeta.map(generatedReport => generatedReport.file.id),
    );
  }, [downloadGeneratedReportFilesArchive, generatedReportsMeta, spawnNotification]);

  const cardActions = React.useMemo(
    () => [
      {
        name: 'Download',
        icon: <DownloadIcon />,
        onAction: (generatedReport: Nullable<GeneratedReportMetaInfoFragment>) => {
          if (!generatedReport?.file) return;
          downloadGeneratedReportFiles(generatedReport.file.id, PiiDataAccessActionType.Downloaded);
        },
      },
    ],
    [downloadGeneratedReportFiles],
  );

  if (loading) {
    return <FullscreenLoader />;
  }

  if (isEmpty(testVerificationRequest)) {
    return <InboxItemEmptyContent />;
  }

  const testVerificationRequestType = renderArrayTypes(testVerificationRequest.type);

  return (
    <React.Fragment>
      <Box p={5}>
        <Typography lineHeight={1.3} variant="subtitle1" fontSize={16}>
          Test Verification of Employment Report
        </Typography>

        <InfoBlock.Container mt={4} loading={employeeInfoForVerificationRequestLoading}>
          <InfoBlock.Item
            title="Employee Name"
            value={employeeInfoForVerificationRequest?.fullName}
          />
          <InfoBlock.Item
            title="Request Created at"
            value={formatDate(
              testVerificationRequest.createdAt,
              DateFormatPatterns.monthDayYearSlashes,
            )}
          />
          <InfoBlock.Item title="Report Type" value={testVerificationRequestType} />
          <InfoBlock.Item
            title="Requested by"
            value={parseNullableValue(testVerificationRequest.platformStaff.user.fullName)}
          />
        </InfoBlock.Container>
      </Box>

      <Divider sx={{ mx: 5 }} />

      <Grid container direction="column" wrap="nowrap" gap={4} p={5}>
        <Grid display="flex" justifyContent="space-between">
          <Typography lineHeight={1.3} variant="subtitle1" fontSize={16}>
            Reports
          </Typography>
          <Button
            disabled={!generatedReportsMeta.length}
            startIcon={<DownloadIcon />}
            onClick={handleDownloadReport}
          >
            Download All
          </Button>
        </Grid>

        <Grid container spacing={2}>
          {generatedReportsMeta.length ? (
            generatedReportsMeta.map(generatedReport => (
              <Grid
                spacing={2}
                key={generatedReport.id}
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                  xl: 2,
                }}
              >
                <GeneratedReportPreviewCard
                  generatedReportMeta={generatedReport}
                  actions={cardActions}
                  onPreview={handlePreview}
                />
              </Grid>
            ))
          ) : (
            <Grid pt={8} textAlign="center" sx={{ margin: '0 auto' }}>
              <Typography variant="subtitle1" fontSize={16}>
                Just a few seconds
              </Typography>
              <Typography color="GrayText" variant="body1">
                Reports are generating
              </Typography>
            </Grid>
          )}
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

const renderArrayTypes = (arrayTypes: VerificationRequestType[]): string => {
  return arrayTypes.reduce((acc: string, currentTitle, index) => {
    const convertedTitle = currentTitle.split('_').join(' ');
    if (index === 0) {
      return convertedTitle;
    }
    return `${acc}, ${convertedTitle}`;
  }, '');
};
